import { Button, Dialog, DialogContent, DialogTitle, Grid, makeStyles } from '@material-ui/core';
import { defer } from '@thalesrc/js-utils';
import React, { useCallback, useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  FirmSubscriptionsModel,
  saveFirmSubscription,
  updateFirmSubscription,
} from 'api/hs/definitions/firm-definitions/firm-subscriptions';
import { AlertContext } from 'context/alert.context';
import { useAsyncEffect } from 'utils';

import Checkbox from '../../../../../Common/Form/Checkbox';
import DateInput from '../../../../../Common/Form/DateInput';
import Select from '../../../../../Common/Form/Select';
import { SelectOptionsContext } from '../../../../../context/select-options.context';

const useStyles = makeStyles(theme => ({
  dialog: {
    width: '100%',
  },
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  sideGrid: {
    display: 'grid',
    gridTemplateColumns: '100%',
    gridTemplateRows: '1fr auto',
  },
  dialogPaper: {
    width: '50vw',
  },
  dialogScroll: {
    alignItems: 'flex-start',
  },
}));

interface Props {
  open: boolean;

  onClose(success: boolean): void;

  data: FirmSubscriptionsModel;
}

export default function FirmForm({ open, onClose, data }: Props) {
  const classes = useStyles();
  const { reset, getValues, setValue, trigger, ...formMethods } = useForm();
  const { showMessage } = useContext(AlertContext);
  const { firms } = useContext(SelectOptionsContext);
  const handleSubmit = useCallback(
    async values => {
      try {
        if (!data?.id) {
          await saveFirmSubscription(values);
          showMessage('Ekleme işlemi başarılı.', 'success');
        } else {
          await updateFirmSubscription({ id: data.id, ...values });
          showMessage('Güncelleme işlemi başarılı.', 'success');
        }
        onClose(true);
      } catch (err) {
        showMessage('İşlem sırasında sorun oluştu.', 'error');
        onClose(false);
      }
    },
    [data, onClose, showMessage]
  );

  useAsyncEffect(async () => {
    if (!open) {
      return;
    }
    await defer();

    if (data) {
      for (const [key, value] of Object.entries(data)) {
        setValue(key, value === null ? '' : value);
      }
      trigger();
    } else {
      reset();
    }
  }, [data, setValue, reset, trigger, open]);

  return (
    <Dialog
      PaperProps={{ style: { maxWidth: 550 } }}
      open={open}
      keepMounted
      onClose={onClose.bind(null, false)}
      classes={{ paper: classes.dialogPaper, scrollPaper: classes.dialogScroll }}>
      <DialogTitle className={classes.dialogTitle}>{data?.id ? 'Firma Kayıt Düzenleme' : 'Firma Kayıt Ekle'}</DialogTitle>
      <DialogContent>
        <FormProvider {...{ ...formMethods, reset, setValue, getValues, trigger }}>
          <form noValidate className="mt-2" onSubmit={formMethods.handleSubmit(handleSubmit)}>
            <Grid container spacing={1}>
              <Grid item md={12} sm={12} lg={12}>
                <Grid item md={12} sm={12} lg={12}>
                  <Select name="firmId" options={firms} label="Firma" required />
                </Grid>
              </Grid>
              <Grid item md={12} sm={12} lg={12}>
                <DateInput required type="date" name="subscriptionStart" label="Başlangıç Tarihi" />
              </Grid>
              <Grid item md={12} sm={12} lg={12}>
                <DateInput required type="date" name="subscriptionEnd" label="Bitiş Tarihi" />
              </Grid>
              <Grid item md={12} sm={12} lg={12}>
                <Checkbox name="active" label="Aktif" />
              </Grid>
              <Grid item md={12} className="text-right">
                <Button variant="contained" type="submit" color="primary" className="mr-2">
                  {data?.id ? 'Güncelle' : 'Kaydet'}
                </Button>
                <Button variant="contained" type="button" onClick={onClose.bind(null, false)}>
                  Vazgeç
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
